import React from 'react'

import './cards.css'
import { getLink, getLinkFromRef, getMargins } from '../../utility'
import { HippostdHtml } from '../blog/blogArticle/componentTypes/HippostdHtml.jsx'
import ButtonLinkCtaList from '../link/buttonLinkCtaList.jsx'
import { BrManageContentButton } from '@bloomreach/react-sdk'
import DOMPurify from 'dompurify'
import CardImage from './cardImage.jsx'
import {
  BGCOLOR,
  ALIGNMENT,
  CARD_OVERLINE,
  CARD_THEME,
  CardUtils,
  CARD_STYLE,
} from './card.util.js'

const CardsWrapper = ({ page, component }) => {
  const _parameters = component?.getParameters()

  const parameter = {
    columns: _parameters.columns || 0,
    backgroundColor: _parameters.backgroundColor || '',
    marginTop: _parameters.marginTop || 'No Spacing',
    marginBottom: _parameters.marginBottom || 'No Spacing',
    alignment: _parameters.alignment || ALIGNMENT.LEFT,
    imagePosition: _parameters.imagePosition || 'Before',
    layout: _parameters.layout || 'Vertical',
    themeColor: _parameters.themeColor || '',
    accentBorder: _parameters.accentBorder || '',
    printable: _parameters.printable || false,
    clickable: _parameters.clickable || false,
    style: _parameters.style || '',
  }

  let items = []
  const _models = component.getModels()
  if (_models?.pagination) {
    const { pagination: _paginationRef } = _models
    const pagination = _paginationRef && page.getContent(_paginationRef)
    if (pagination) {
      const itemRefs = pagination?.getItems()
      const documents = itemRefs
        ?.map((itemRef) => itemRef && page.getContent(itemRef))
        ?.filter((doc) => !!doc)
      items = documents?.map((doc) => {
        const data = doc.getData()
        const imageSrc = data?.imageDetails?.image
          ? page?.getContent(data?.imageDetails?.image)
          : null
        const bgImage = data?.bgImage
          ? page?.getContent(data?.bgImage)?.getOriginal()?.getUrl()
          : null

        return {
          id: data?.id,
          title: data?.title || '',
          subTitle: data?.subTitle || '',
          body: data?.body?.html?.value || '',
          eyebrow: data?.eyebrow || '',
          overlineColor:
            CARD_OVERLINE?.[data?.overlineColor?.trim() || ''] || '',
          overlineEyebrow: data?.overlineEyebrow || '',
          bgColor: data?.bgColor || '',
          bgImage: bgImage || '',
          bgImageOverlay: data?.bgImageOverlay || false,
          cardLink: data?.cardLink
            ? getLink(getLinkFromRef(data?.cardLink, page), null)
            : null,
          linkList:
            data?.links?.length > 0
              ? data?.links
                  ?.filter((l) => l.link && l.link.length > 0)
                  ?.map((l) => getLink(getLinkFromRef(l, page), null))
              : [],
          image: {
            ...data?.imageDetails,
            image: imageSrc,
            alignment:
              data?.imageDetails?.alignment &&
              data?.imageDetails?.alignment !== ALIGNMENT.Default
                ? data?.imageDetails?.alignment
                : parameter.alignment,
            resolution:
              data?.imageDetails?.resolution &&
              data?.imageDetails?.resolution !== 'default'
                ? data?.imageDetails?.resolution
                : 'medium',
            svgHref: data?.imageDetails?.svg?.svgPath
              ? page.getContent(data?.imageDetails?.svg?.svgPath)?.data?.asset
                  ?.links?.site?.href
              : '',
            svgFallbackImage: data?.imageDetails?.svg?.fallbackImage
              ? page.getContent(data?.imageDetails?.svg?.fallbackImage)
              : null,
          },
          document: doc,
        }
      })
    }
  }

  const textAlignment =
    parameter.alignment === ALIGNMENT.LEFT
      ? 'u-textLeft'
      : parameter.alignment === ALIGNMENT.CENTER
        ? 'u-textCenter'
        : ''
  const buttonAlignment =
    parameter.alignment === ALIGNMENT.LEFT
      ? 'u-flexJustifyStart'
      : parameter.alignment === ALIGNMENT.CENTER
        ? 'u-flexJustifyCenter'
        : ''

  const headingTextColor = CardUtils.getHeadingTextColor(
    parameter.backgroundColor
  )

  const bodyTextColor = CardUtils.getBodyTextColor(parameter.backgroundColor)

  const border = CardUtils.getBorder(parameter.backgroundColor)

  const background = CardUtils.getBackground(parameter.backgroundColor)

  const columns = parameter.columns || (items.length > 4 ? 4 : items.length)

  const beforFlex =
    parameter.layout === 'Horizontal'
      ? 'u-sm-flexCol u-flexRow'
      : 'u-sm-flexCol u-flexCol'

  const afterFlex =
    parameter.layout === 'Horizontal'
      ? 'u-sm-flexColReverse u-flexRowReverse'
      : 'u-sm-flexColReverse u-flexColReverse'

  const cardLayout = parameter.imagePosition === 'After' ? afterFlex : beforFlex

  const constantPadding = '4gu'

  const cardPadding = CardUtils.getCardPadding(
    parameter.backgroundColor,
    constantPadding
  )

  const cardHeaderPadding = CardUtils.getCardHeaderPadding(
    parameter.backgroundColor,
    parameter.layout,
    parameter.imagePosition,
    constantPadding
  )

  const accentBorderStyle = CardUtils.getAccentBorderStyle(
    parameter.backgroundColor,
    parameter.layout,
    parameter.imagePosition,
    parameter.accentBorder
  )

  const styleConfig = {
    textAlignment,
    buttonAlignment,
    headingTextColor,
    bodyTextColor,
    border,
    cardPadding,
    background,
    cardLayout,
    cardHeaderPadding,
    accentBorderStyle,
    cardStyle: parameter.style,
  }

  const config = {
    ...parameter,
    layout: parameter.layout,
    imagePosition: parameter.imagePosition,
    isVertical: parameter.layout === 'Vertical',
    isHorizontal: parameter.layout === 'Horizontal',
    isDarkTheme: CardUtils.getIsDarkTheme(parameter.backgroundColor),
    themeColor: _parameters.themeColor?.trim() || '',
    accentBorder: _parameters.accentBorder || '',
    clickable: parameter.clickable || false,
    columns,
  }

  return (
    <div
      className={`Cards u-sizeConstrained ${parameter.printable ? '' : 'u-printHidden'} ${getMargins(parameter.marginTop, parameter.marginBottom)}`}
    >
      <Cards
        items={items}
        columns={columns}
        styleConfig={styleConfig}
        config={{ ...config }}
      />
    </div>
  )
}

export default CardsWrapper

const Cards = ({ items = [], columns = 0, styleConfig = {}, config = {} }) => {
  return (
    <div className="Grid Grid--withGutter">
      {items.map((item) => {
        return (
          <CardDisplay
            key={item.id}
            data={{
              title: item.title,
              subTitle: item.subTitle,
              body: item.body,
              cardLink: item.cardLink,
              linkList: item.linkList,
              image: item.image,
              eyebrow: item.eyebrow,
              overlineColor: item?.overlineColor || '',
              overlineEyebrow: item?.overlineEyebrow || false,
              document: item.document,
            }}
            columns={columns}
            styleConfig={{
              ...styleConfig,
              headingTextColor:
                item.bgColor && item.bgColor !== 'Default'
                  ? CardUtils.getHeadingTextColor(item.bgColor)
                  : styleConfig.headingTextColor,
              bodyTextColor:
                item.bgColor && item.bgColor !== 'Default'
                  ? CardUtils.getBodyTextColor(item.bgColor)
                  : styleConfig.bodyTextColor,
              border:
                item.bgColor && item.bgColor !== 'Default'
                  ? CardUtils.getBorder(item.bgColor)
                  : styleConfig.border,
              cardPadding:
                item.bgColor && item.bgColor !== 'Default'
                  ? CardUtils.getCardPadding(item.bgColor)
                  : styleConfig.cardPadding,
              background:
                item.bgColor && item.bgColor !== 'Default'
                  ? CardUtils.getBackground(item.bgColor) ||
                    styleConfig.background
                  : styleConfig.background,
              bgImage: item.bgImage || '',
              bgImageOverlay: item.bgImageOverlay || false,
              cardHeaderPadding:
                item.bgColor && item.bgColor !== 'Default'
                  ? CardUtils.getCardHeaderPadding(
                      item.bgColor,
                      config.layout,
                      config.imagePosition
                    )
                  : styleConfig.cardHeaderPadding,
              accentBorderStyle:
                item.bgColor && item.bgColor !== 'Default'
                  ? CardUtils.getAccentBorderStyle(
                      item.bgColor,
                      config.layout,
                      config.imagePosition,
                      config.accentBorder
                    )
                  : styleConfig.accentBorderStyle,
            }}
            config={{
              ...config,
              isDarkTheme:
                item.bgColor && item.bgColor !== 'Default'
                  ? CardUtils.getIsDarkTheme(item.bgColor)
                  : config.isDarkTheme,
            }}
          />
        )
      })}
    </div>
  )
}

const CardDisplay = ({ data = {}, styleConfig = {}, config = {} }) => {
  const {
    background = '',
    bgImage = '',
    bodyTextColor = 'u-textColorSlateDark',
    border = '',
    buttonAlignment = 'u-flexJustifyStart',
    cardHeaderPadding = 'u-paddingBottom4gu',
    cardLayout = 'u-sm-flexCol u-flexCol',
    cardPadding = '',
    headingTextColor = 'u-textColorBrandDark',
    textAlignment = 'u-textLeft',
    accentBorderStyle = '',
    bgImageOverlay = false,
    cardStyle = '',
  } = styleConfig || {}

  const {
    title = '',
    subTitle = '',
    body = '',
    cardLink = null,
    linkList = [],
    image = {},
    eyebrow = '',
    overlineColor = '',
    overlineEyebrow = false,
    document = null,
  } = data || {}

  const {
    columns = 3,
    isHorizontal,
    isDarkTheme = false,
    themeColor = '',
    accentBorder = '',
    clickable = false,
  } = config || {}
  const clickableLink = (clickable && cardLink) || null

  const CardWrapper = clickableLink
    ? ({ children, className }) => (
        <a
          href={DOMPurify.sanitize(clickableLink.url)}
          target={clickableLink.target || '_self'}
          rel={
            clickableLink.target === '_blank'
              ? 'noopener noreferrer'
              : undefined
          }
          className={className}
          data-analytics={clickableLink?.analyticsAttribute}
          style={{
            textDecoration: 'none',
            color: 'inherit',
            cursor: 'pointer',
          }}
        >
          {children}
        </a>
      )
    : ({ children, className }) => <div className={className}>{children}</div>

  return (
    <CardWrapper
      className={`u-flex u-flexCol u-sm-sizeFull u-md-size1of${columns} u-lg-size1of${columns} u-posRelative`}
    >
      {document && <BrManageContentButton content={document} />}
      <div
        className={`card u-flex ${cardLayout} ${background} ${border} ${clickableLink ? 'card-hover' : ''} ${bgImage && bgImageOverlay ? 'card-bg-overlay' : ''} ${CARD_STYLE?.[cardStyle]?.card || ''}`}
        style={
          bgImage
            ? {
                backgroundImage: `url(${bgImage})`,
                backgroundSize: 'cover',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center top',
              }
            : {}
        }
      >
        {themeColor && accentBorder && (
          <div
            className={`card-accent-border ${accentBorderStyle} ${themeColor ? CARD_THEME?.[themeColor]?.bg : ''}`}
          />
        )}
        {(image?.image || image?.svgHref) && (
          <div
            className={`card-header ${cardHeaderPadding} ${isHorizontal ? 'horizontal' : ''}`}
          >
            <CardImage image={image?.image} {...image} />
          </div>
        )}
        <div
          className={`card-content u-flex u-flexCol u-flexGrow1 ${cardPadding} ${CARD_STYLE?.[cardStyle]?.content || ''}`}
        >
          <div className="u-flexGrow1">
            {eyebrow && (
              <div
                className={`eyebrow u-textSizeMsrm1 u-textPrimary u-textBold u-marginBottom2gu u-inlineBlock ${themeColor ? CARD_THEME?.[themeColor]?.text : headingTextColor}`}
              >
                {overlineEyebrow && (
                  <div
                    className={`overline u-marginBottom1gu ${overlineColor || (isDarkTheme ? CARD_OVERLINE.white : CARD_OVERLINE.brandDark)}`}
                  />
                )}
                {eyebrow}
              </div>
            )}
            {title && (
              <h3
                className={`${textAlignment} ${themeColor ? CARD_THEME?.[themeColor]?.text : headingTextColor} ${CARD_STYLE?.[cardStyle]?.heading || 'u-textBold'}`}
              >
                {title}
              </h3>
            )}
            <p className={`u-textSizeMsrm1 ${bodyTextColor}`}>{subTitle}</p>
            <HippostdHtml
              value={body}
              classNames={`${textAlignment} ${bodyTextColor} ${CARD_STYLE?.[cardStyle]?.body || ''}`}
            />
          </div>
          <div className={`card-links`}>
            {linkList && linkList.length > 0 && (
              <ButtonLinkCtaList
                ctaWrapperClassNames={`${buttonAlignment} ${CARD_STYLE?.[cardStyle]?.buttons || 'u-marginTop2gu'}`}
                linkList={linkList}
              />
            )}
          </div>
        </div>
      </div>
    </CardWrapper>
  )
}
